import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Plus, X, Trash2, Edit, Eye, Users, Calendar, Gift, CheckCircle, CreditCard, FileText, Globe } from 'lucide-react';
import axios from 'axios';
import Swal from 'sweetalert2';
import JoditEditor from 'jodit-react';

const API_BASE_URL = 'https://backend.dlapakistan.com/api';

const AddStoreCards = () => {
  // Tab state
  const [activeTab, setActiveTab] = useState('cards');
  
  // Store Cards States
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentCard, setCurrentCard] = useState(null);
  const [loading, setLoading] = useState(false);
  const [cards, setCards] = useState([]);
  const [customStaffSize, setCustomStaffSize] = useState(false);
  
  const [formData, setFormData] = useState({
    labCategoryName: '',
    monthlyFee: '',
    staffSize: '',
    annualTotal: '',
    keyPerks: '',
  });

  // Content States
  const [isContentModalOpen, setIsContentModalOpen] = useState(false);
  const [isContentViewModalOpen, setIsContentViewModalOpen] = useState(false);
  const [isContentEditMode, setIsContentEditMode] = useState(false);
  const [currentContent, setCurrentContent] = useState(null);
  const [contents, setContents] = useState([]);
  const [loadingContent, setLoadingContent] = useState(false);
  const [showOnlyPublished, setShowOnlyPublished] = useState(false);
  const [saving, setSaving] = useState(false);
  
  const editorRef = useRef(null);
  
  const [contentFormData, setContentFormData] = useState({
    content: '',
    is_published: true,
  });

  // Staff size options (predefined ranges)
  const staffSizeOptions = [
    '1-3 accounts',
    '4-10 accounts', 
    '11-20 accounts',
    '21-50 accounts',
    '51-100 accounts',
    '100+ accounts'
  ];

  // Define fetchCards with useCallback to prevent ESLint warnings
  const fetchCards = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/store-cards/`);
      if (response.data.success) {
        setCards(response.data.data);
      } else {
        setCards([]);
      }
    } catch (err) {
      console.error('Fetch error:', err);
      Swal.fire({
        title: 'Error',
        text: err.response?.data?.error || 'Failed to load cards',
        icon: 'error',
        timer: 3000
      });
      setCards([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Define fetchContents with useCallback to prevent ESLint warnings
  const fetchContents = useCallback(async () => {
    try {
      setLoadingContent(true);
      const url = showOnlyPublished 
        ? `${API_BASE_URL}/store-content/?published_only=true`
        : `${API_BASE_URL}/store-content/`;
      const response = await axios.get(url);
      if (response.data.success) {
        setContents(response.data.data);
      } else {
        setContents([]);
      }
    } catch (err) {
      console.error('Fetch error:', err);
      Swal.fire({
        title: 'Error',
        text: err.response?.data?.error || 'Failed to load content',
        icon: 'error',
        timer: 3000
      });
      setContents([]);
    } finally {
      setLoadingContent(false);
    }
  }, [showOnlyPublished]);

  useEffect(() => {
    fetchCards();
  }, [fetchCards]);

  useEffect(() => {
    if (activeTab === 'content') {
      fetchContents();
    }
  }, [activeTab, fetchContents]);

  // Jodit Editor Configuration
  const config = useMemo(() => ({
    readonly: false,
    height: 400,
    theme: "default",
    toolbarButtonSize: "medium",
    toolbarSticky: true,
    toolbarAdaptive: true,
    showTooltip: true,
    enableDragAndDrop: true,
    saveModeInStorage: false,
    spellcheck: true,
    askBeforePasteHTML: false,
    askBeforePasteFromWord: false,
    defaultMode: 1,
    
    buttons: [
      'source', '|',
      'bold', 'italic', 'underline', 'strikethrough', '|',
      'superscript', 'subscript', '|',
      'ul', 'ol', '|',
      'outdent', 'indent', '|',
      'font', 'fontsize', 'brush', 'paragraph', '|',
      'image', 'table', 'link', '|',
      'align', 'undo', 'redo', '|',
      'hr', 'eraser', 'copyformat', '|',
      'fullsize', 'print'
    ],
    
    font: [
      'Arial', 'Helvetica', 'Verdana', 'Tahoma', 'Trebuchet MS', 
      'Times New Roman', 'Georgia', 'Courier New', 'Impact', 
      'Comic Sans MS', 'monospace', 'cursive', 'sans-serif', 'serif'
    ],
    
    fontSize: ['8', '9', '10', '11', '12', '14', '16', '18', '20', '22', '24', '26', '28', '32', '36', '42', '48', '54', '60', '72'],
    
    defaultFontSize: '14px',
    defaultFontName: 'Arial',
    
    colors: {
      greyscale: ['#000000', '#434343', '#666666', '#999999', '#B7B7B7', '#CCCCCC', '#E5E5E5', '#F7F7F7', '#FFFFFF'],
      palette: [
        '#980000', '#FF0000', '#FF9900', '#FFFF00', '#00F0F0', '#00FFFF', '#00FF00', 
        '#0000FF', '#4A86E8', '#6AA84F', '#FFE599', '#FF9900', '#E69138', '#B45F4B', 
        '#A64D79', '#F9CB9C', '#B6D7A8', '#9FC5E8', '#A2C4C9', '#C27BA0', '#674EA7', '#3D85C6'
      ]
    },
    
    uploader: {
      insertImageAsBase64URI: true,
      imagesExtensions: ['jpg', 'png', 'jpeg', 'gif', 'svg', 'webp', 'bmp']
    },
    
    placeholder: 'Write your content here... Use the toolbar above to format text, add images, create lists, and more!',
    removeButtons: ['about'],
    beautyHTML: true,
    zIndex: 100000,
  }), []);

  const handleMonthlyFeeChange = (value) => {
    const monthly = parseFloat(value);
    if (!isNaN(monthly) && monthly > 0) {
      const annual = monthly * 12;
      setFormData(prev => ({
        ...prev,
        monthlyFee: value,
        annualTotal: annual.toString()
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        monthlyFee: value,
        annualTotal: ''
      }));
    }
  };

  const handleStaffSizeChange = (value) => {
    if (value === 'custom') {
      setCustomStaffSize(true);
      setFormData(prev => ({ ...prev, staffSize: '' }));
    } else {
      setCustomStaffSize(false);
      setFormData(prev => ({ ...prev, staffSize: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.labCategoryName || !formData.labCategoryName.trim()) {
      Swal.fire({ title: 'Error', text: 'Please enter lab category name', icon: 'error', timer: 3000 });
      return;
    }
    
    if (!formData.monthlyFee || parseFloat(formData.monthlyFee) <= 0) {
      Swal.fire({ title: 'Error', text: 'Please enter a valid monthly fee', icon: 'error', timer: 3000 });
      return;
    }
    
    if (!formData.staffSize || !formData.staffSize.trim()) {
      Swal.fire({ title: 'Error', text: 'Please select or enter staff size', icon: 'error', timer: 3000 });
      return;
    }
    
    if (!formData.keyPerks || !formData.keyPerks.trim()) {
      Swal.fire({ title: 'Error', text: 'Please enter key perks/features', icon: 'error', timer: 3000 });
      return;
    }

    const dataToSend = {
      lab_category_name: formData.labCategoryName.trim(),
      monthly_fee: parseFloat(formData.monthlyFee),
      staff_size: formData.staffSize.trim(),
      key_perks: formData.keyPerks.trim(),
    };

    try {
      if (isEditMode && currentCard) {
        const response = await axios.put(`${API_BASE_URL}/store-cards/${currentCard.id}/`, dataToSend);
        if (response.data.success) {
          Swal.fire({ title: 'Success', text: 'Card updated successfully!', icon: 'success', timer: 2000, showConfirmButton: false });
          await fetchCards();
          handleCloseModal();
        }
      } else {
        const response = await axios.post(`${API_BASE_URL}/store-cards/`, dataToSend);
        if (response.data.success) {
          Swal.fire({ title: 'Success', text: 'Card added successfully!', icon: 'success', timer: 2000, showConfirmButton: false });
          await fetchCards();
          handleCloseModal();
        }
      }
    } catch (err) {
      console.error('Error:', err);
      Swal.fire({ title: 'Error', text: 'Failed to save card', icon: 'error', timer: 5000 });
    }
  };

  const handleDeleteCard = async (cardId) => {
    const confirmed = await Swal.fire({
      title: 'Are you sure?',
      text: 'This action cannot be undone',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Delete',
      cancelButtonText: 'Cancel'
    });

    if (confirmed.isConfirmed) {
      try {
        const response = await axios.delete(`${API_BASE_URL}/store-cards/${cardId}/`);
        if (response.data.success) {
          await fetchCards();
          Swal.fire({ title: 'Deleted!', text: 'Card has been deleted.', icon: 'success', timer: 2000, showConfirmButton: false });
        }
      } catch (err) {
        Swal.fire({ title: 'Error', text: 'Failed to delete card', icon: 'error', timer: 3000 });
      }
    }
  };

  const handleEditCard = (card) => {
    setCurrentCard(card);
    const isCustom = !staffSizeOptions.includes(card.staff_size);
    setCustomStaffSize(isCustom);
    setFormData({
      labCategoryName: card.lab_category_name,
      monthlyFee: card.monthly_fee.toString(),
      staffSize: card.staff_size,
      annualTotal: card.annual_total.toString(),
      keyPerks: card.key_perks,
    });
    setIsEditMode(true);
    setIsModalOpen(true);
  };

  const handleViewCard = (card) => {
    setCurrentCard(card);
    setIsViewModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setIsEditMode(false);
    setCurrentCard(null);
    setCustomStaffSize(false);
    setFormData({
      labCategoryName: '',
      monthlyFee: '',
      staffSize: '',
      annualTotal: '',
      keyPerks: '',
    });
  };

  const handleCloseViewModal = () => {
    setIsViewModalOpen(false);
    setCurrentCard(null);
  };

  // ==================== CONTENT FUNCTIONS ====================
  
  const handleEditorChange = (newContent) => {
    setContentFormData(prev => ({
      ...prev,
      content: newContent
    }));
  };

  const handleContentSubmit = async (e) => {
    e.preventDefault();
    
    if (!contentFormData.content || !contentFormData.content.trim()) {
      Swal.fire({ title: 'Error', text: 'Please enter content', icon: 'error', timer: 3000 });
      return;
    }

    const dataToSend = {
      content: contentFormData.content,
      is_published: contentFormData.is_published,
    };

    try {
      setSaving(true);
      
      if (isContentEditMode && currentContent) {
        const response = await axios.put(`${API_BASE_URL}/store-content/${currentContent.id}/`, dataToSend);
        if (response.data.success) {
          Swal.fire({ title: 'Success', text: 'Content updated successfully!', icon: 'success', timer: 2000, showConfirmButton: false });
          await fetchContents();
          handleCloseContentModal();
        }
      } else {
        const response = await axios.post(`${API_BASE_URL}/store-content/`, dataToSend);
        if (response.data.success) {
          Swal.fire({ title: 'Success', text: 'Content added successfully!', icon: 'success', timer: 2000, showConfirmButton: false });
          await fetchContents();
          handleCloseContentModal();
        }
      }
    } catch (err) {
      console.error('Error:', err);
      Swal.fire({ title: 'Error', text: 'Failed to save content', icon: 'error', timer: 5000 });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteContent = async (contentId) => {
    const confirmed = await Swal.fire({
      title: 'Are you sure?',
      text: 'This action cannot be undone',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Delete',
      cancelButtonText: 'Cancel'
    });

    if (confirmed.isConfirmed) {
      try {
        const response = await axios.delete(`${API_BASE_URL}/store-content/${contentId}/`);
        if (response.data.success) {
          await fetchContents();
          Swal.fire({ title: 'Deleted!', text: 'Content has been deleted.', icon: 'success', timer: 2000, showConfirmButton: false });
        }
      } catch (err) {
        Swal.fire({ title: 'Error', text: 'Failed to delete content', icon: 'error', timer: 3000 });
      }
    }
  };

  const handleEditContent = (content) => {
    setCurrentContent(content);
    setContentFormData({
      content: content.content,
      is_published: content.is_published,
    });
    setIsContentEditMode(true);
    setIsContentModalOpen(true);
  };

  const handleViewContent = (content) => {
    setCurrentContent(content);
    setIsContentViewModalOpen(true);
  };

  const handleCloseContentModal = () => {
    setIsContentModalOpen(false);
    setIsContentEditMode(false);
    setCurrentContent(null);
    setContentFormData({
      content: '',
      is_published: true,
    });
  };

  const handleCloseContentViewModal = () => {
    setIsContentViewModalOpen(false);
    setCurrentContent(null);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-PK', {
      style: 'currency',
      currency: 'PKR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-PK', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-PK', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const renderKeyPerks = (perksText) => {
    const perks = perksText.split('\n').filter(perk => perk.trim());
    return (
      <div className="space-y-2">
        {perks.map((perk, index) => (
          <div key={index} className="flex items-start gap-2">
            <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
            <span className="text-gray-600 text-sm">{perk.replace('✓', '').trim()}</span>
          </div>
        ))}
      </div>
    );
  };

  const renderEmptyState = () => (
    <div className="text-center py-16">
      <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-gradient-to-br from-emerald-50 to-green-50 flex items-center justify-center">
        <Plus className="w-10 h-10 text-emerald-400" />
      </div>
      <h3 className="text-2xl font-semibold text-gray-700 mb-2">No Premium Cards Yet</h3>
      <p className="text-gray-500 mb-8 max-w-md mx-auto">Get started by adding your first premium lab package or subscription plan</p>
      <button
        onClick={() => {
          setIsEditMode(false);
          setCustomStaffSize(false);
          setIsModalOpen(true);
        }}
        className="px-8 py-3 rounded-xl bg-gradient-to-r from-emerald-500 to-green-500 text-white font-medium hover:from-emerald-600 hover:to-green-600 transition-all duration-300 shadow-md hover:shadow-lg"
      >
        <Plus className="w-5 h-5 inline mr-2" />
        Add Your First Card
      </button>
    </div>
  );

  const renderContentEmptyState = () => (
    <div className="text-center py-16">
      <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-gradient-to-br from-emerald-50 to-green-50 flex items-center justify-center">
        <FileText className="w-10 h-10 text-emerald-400" />
      </div>
      <h3 className="text-2xl font-semibold text-gray-700 mb-2">No Content Yet</h3>
      <p className="text-gray-500 mb-8 max-w-md mx-auto">Create your first content piece with our rich text editor. Add headings, images, lists, and more!</p>
      <button
        onClick={() => {
          setIsContentEditMode(false);
          setIsContentModalOpen(true);
        }}
        className="px-8 py-3 rounded-xl bg-gradient-to-r from-emerald-500 to-green-500 text-white font-medium hover:from-emerald-600 hover:to-green-600 transition-all duration-300 shadow-md hover:shadow-lg"
      >
        <Plus className="w-5 h-5 inline mr-2" />
        Create First Content
      </button>
    </div>
  );

  const renderGrid = () => (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mt-8">
      {Array.isArray(cards) && cards.map((card) => (
        <div key={card.id} className="group relative bg-white rounded-2xl border border-gray-200 hover:border-emerald-300 hover:shadow-xl transition-all duration-300 overflow-hidden flex flex-col h-full transform hover:-translate-y-1">
          <div className="bg-gradient-to-r from-emerald-500 to-green-500 p-5">
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <h3 className="text-xl font-bold text-white mb-1 line-clamp-2">{card.lab_category_name}</h3>
                <div className="flex items-center gap-2 mt-2">
                  <Users className="w-4 h-4 text-emerald-100" />
                  <p className="text-emerald-100 text-sm">{card.staff_size}</p>
                </div>
              </div>
              <div className="flex space-x-1 ml-2">
                <button onClick={(e) => { e.stopPropagation(); handleEditCard(card); }} className="p-2 bg-white/20 backdrop-blur-sm rounded-lg hover:bg-white/30 shadow-sm transition-all duration-200 hover:scale-105" title="Edit">
                  <Edit className="w-4 h-4 text-white" />
                </button>
                <button onClick={(e) => { e.stopPropagation(); handleDeleteCard(card.id); }} className="p-2 bg-white/20 backdrop-blur-sm rounded-lg hover:bg-red-500/50 shadow-sm transition-all duration-200 hover:scale-105" title="Delete">
                  <Trash2 className="w-4 h-4 text-white" />
                </button>
              </div>
            </div>
          </div>
          <div className="p-5 flex-1 cursor-pointer" onClick={() => handleViewCard(card)}>
            <div className="mb-5 pb-4 border-b border-gray-100">
              <div className="flex items-baseline justify-between mb-2">
                <div>
                  <span className="text-3xl font-bold text-gray-900">{formatCurrency(parseFloat(card.monthly_fee))}</span>
                  <span className="text-gray-500 text-sm ml-1">/month</span>
                </div>
              </div>
              <div className="mt-2">
                <span className="text-sm text-gray-500">Annual billing: </span>
                <span className="text-base font-semibold text-emerald-600">{formatCurrency(parseFloat(card.annual_total))}</span>
              </div>
            </div>
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Gift className="w-4 h-4 text-emerald-500" />
                <span className="font-semibold text-gray-700 text-sm">Key Features</span>
              </div>
              <div className="space-y-2">
                {card.key_perks.split('\n').slice(0, 4).map((perk, idx) => (
                  <div key={idx} className="flex items-start gap-2">
                    <CheckCircle className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-600 text-xs line-clamp-1">{perk.replace('✓', '').trim()}</span>
                  </div>
                ))}
                {card.key_perks.split('\n').length > 4 && (
                  <p className="text-xs text-emerald-500 mt-2 font-medium">+{card.key_perks.split('\n').length - 4} more features</p>
                )}
              </div>
            </div>
          </div>
          <div className="p-4 bg-gray-50/50 border-t border-gray-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1">
                <Calendar className="w-3 h-3 text-gray-400" />
                <span className="text-xs text-gray-500">Updated {formatDate(card.updated_at)}</span>
              </div>
              <button onClick={() => handleViewCard(card)} className="text-emerald-500 hover:text-emerald-600 transition-colors">
                <Eye className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  const renderContentList = () => (
    <div className="space-y-4 mt-8">
      {Array.isArray(contents) && contents.map((content) => (
        <div key={content.id} className="bg-white rounded-xl border border-gray-200 hover:border-emerald-300 hover:shadow-lg transition-all duration-300 overflow-hidden">
          <div className="p-5">
            <div className="flex items-start justify-between">
              <div className="flex-1 cursor-pointer" onClick={() => handleViewContent(content)}>
                <div className="flex items-center gap-3 mb-3">
                  {content.is_published ? (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      <Globe className="w-3 h-3 mr-1" />
                      Published
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                      Draft
                    </span>
                  )}
                  <span className="text-xs text-gray-500">Content #{content.id}</span>
                </div>
                <div className="text-gray-600 text-sm mb-3 line-clamp-3" 
                     dangerouslySetInnerHTML={{ __html: content.content.substring(0, 200) + '...' }} />
                <div className="flex items-center gap-4 text-xs text-gray-500">
                  <div className="flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    <span>Created: {formatDateTime(content.created_at)}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    <span>Updated: {formatDateTime(content.updated_at)}</span>
                  </div>
                </div>
              </div>
              
              <div className="flex items-center gap-2 ml-4">
                <button onClick={() => handleEditContent(content)} className="p-2 hover:bg-emerald-50 rounded-lg transition-colors" title="Edit">
                  <Edit className="w-4 h-4 text-emerald-600" />
                </button>
                <button onClick={() => handleDeleteContent(content.id)} className="p-2 hover:bg-red-50 rounded-lg transition-colors" title="Delete">
                  <Trash2 className="w-4 h-4 text-red-600" />
                </button>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div className="flex-1 bg-gray-50/30">
      {/* Main Header */}
      <div className="bg-white px-8 py-6 border-b border-gray-100 shadow-sm">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Store Management</h1>
            <p className="text-gray-500 mt-1">Manage premium cards and website content</p>
          </div>
          
          {activeTab === 'cards' && (
            <button onClick={() => { setIsEditMode(false); setCustomStaffSize(false); setIsModalOpen(true); }} className="flex items-center px-6 py-3 rounded-xl bg-gradient-to-r from-emerald-500 to-green-500 hover:from-emerald-600 hover:to-green-600 text-white font-medium transition-all duration-300 shadow-md hover:shadow-lg">
              <Plus className="w-5 h-5 mr-2" />
              Add Premium Card
            </button>
          )}
          
          {activeTab === 'content' && (
            <div className="flex gap-3">
              <button onClick={() => setShowOnlyPublished(!showOnlyPublished)} className={`flex items-center px-4 py-2 rounded-xl transition-all duration-300 ${showOnlyPublished ? 'bg-emerald-500 text-white shadow-md' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
                <Globe className="w-4 h-4 mr-2" />
                {showOnlyPublished ? 'Showing Published' : 'Show All'}
              </button>
              <button onClick={() => { setIsContentEditMode(false); setIsContentModalOpen(true); }} className="flex items-center px-6 py-2 rounded-xl bg-gradient-to-r from-emerald-500 to-green-500 hover:from-emerald-600 hover:to-green-600 text-white font-medium transition-all duration-300 shadow-md hover:shadow-lg">
                <Plus className="w-5 h-5 mr-2" />
                Add Content
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-20">
        <div className="px-8">
          <div className="flex space-x-8">
            <button onClick={() => setActiveTab('cards')} className={`flex items-center gap-2 py-4 px-2 border-b-2 transition-all duration-300 ${activeTab === 'cards' ? 'border-emerald-500 text-emerald-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}>
              <CreditCard className="w-5 h-5" />
              <span className="font-medium">Premium Cards</span>
              <span className="ml-1 text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">Packages</span>
            </button>
            <button onClick={() => setActiveTab('content')} className={`flex items-center gap-2 py-4 px-2 border-b-2 transition-all duration-300 ${activeTab === 'content' ? 'border-emerald-500 text-emerald-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}>
              <FileText className="w-5 h-5" />
              <span className="font-medium">Content Manager</span>
              <span className="ml-1 text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">Rich Text</span>
            </button>
          </div>
        </div>
      </div>

      {/* Cards Tab Content */}
      {activeTab === 'cards' && (
        <div className="p-8">
          {loading ? (
            <div className="text-center py-16"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500 mx-auto"></div><p className="mt-4 text-gray-600">Loading premium cards...</p></div>
          ) : cards.length === 0 ? renderEmptyState() : renderGrid()}
        </div>
      )}

      {/* Content Tab */}
      {activeTab === 'content' && (
        <div className="p-8">
          {loadingContent ? (
            <div className="text-center py-16"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500 mx-auto"></div><p className="mt-4 text-gray-600">Loading content...</p></div>
          ) : contents.length === 0 ? renderContentEmptyState() : renderContentList()}
        </div>
      )}

      {/* Add/Edit Card Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 overflow-y-auto">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl my-8">
            <div className="px-6 py-5 border-b border-gray-100 flex justify-between items-center sticky top-0 bg-white rounded-t-2xl">
              <h2 className="text-2xl font-bold text-gray-900">{isEditMode ? 'Edit' : 'Add'} Premium Card</h2>
              <button onClick={handleCloseModal} className="p-2 hover:bg-gray-100 rounded-xl transition-colors"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={handleSubmit} className="max-h-[calc(100vh-200px)] overflow-y-auto">
              <div className="p-6 space-y-6">
                <div><label className="block text-sm font-semibold text-gray-700 mb-2">Lab Category Name *</label><input type="text" value={formData.labCategoryName} onChange={(e) => setFormData(prev => ({ ...prev, labCategoryName: e.target.value }))} className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 focus:outline-none transition-all duration-300" required placeholder="Enter lab category name" /></div>
                <div><label className="block text-sm font-semibold text-gray-700 mb-2">Monthly Fee (PKR) *</label><div className="relative"><span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 font-semibold">Rs.</span><input type="number" value={formData.monthlyFee} onChange={(e) => handleMonthlyFeeChange(e.target.value)} className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 focus:outline-none transition-all duration-300" required placeholder="Enter monthly fee" min="0" step="1" /></div></div>
                <div><label className="block text-sm font-semibold text-gray-700 mb-2">Employee Accounts *</label><div className="relative"><Users className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" /><select value={customStaffSize ? 'custom' : (formData.staffSize && staffSizeOptions.includes(formData.staffSize) ? formData.staffSize : '')} onChange={(e) => handleStaffSizeChange(e.target.value)} className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 focus:outline-none transition-all duration-300 appearance-none bg-white"><option value="">Select employee accounts</option>{staffSizeOptions.map(option => (<option key={option} value={option}>{option}</option>))}<option value="custom">Custom (Enter your own)</option></select></div>{customStaffSize && (<div className="mt-3"><input type="text" value={formData.staffSize} onChange={(e) => setFormData(prev => ({ ...prev, staffSize: e.target.value }))} className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 focus:outline-none transition-all duration-300" placeholder="Enter custom accounts" required /></div>)}</div>
                <div><label className="block text-sm font-semibold text-gray-700 mb-2">Annual Total (PKR) - Auto Calculated</label><div className="relative"><span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 font-semibold">Rs.</span><input type="text" value={formData.annualTotal ? formatCurrency(parseFloat(formData.annualTotal)) : ''} className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-600 cursor-not-allowed" disabled /></div></div>
                <div><label className="block text-sm font-semibold text-gray-700 mb-2">Key Perks / Features *</label><textarea value={formData.keyPerks} onChange={(e) => setFormData(prev => ({ ...prev, keyPerks: e.target.value }))} className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 focus:outline-none transition-all duration-300" rows="6" required placeholder="Enter each perk on a new line" /></div>
              </div>
              <div className="px-6 py-5 border-t border-gray-100 flex justify-end space-x-3 sticky bottom-0 bg-white rounded-b-2xl">
                <button type="button" onClick={handleCloseModal} className="px-6 py-2.5 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-all duration-300 font-medium">Cancel</button>
                <button type="submit" className="px-6 py-2.5 bg-emerald-500 text-white rounded-xl hover:bg-emerald-600 transition-all duration-300 font-medium shadow-md hover:shadow-lg">{isEditMode ? 'Update Card' : 'Add Card'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Card Modal */}
      {isViewModalOpen && currentCard && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 overflow-y-auto">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl my-8">
            <div className="px-6 py-5 border-b border-gray-100 flex justify-between items-center sticky top-0 bg-white rounded-t-2xl">
              <h2 className="text-2xl font-bold text-gray-900">Premium Card Details</h2>
              <button onClick={handleCloseViewModal} className="p-2 hover:bg-gray-100 rounded-xl transition-colors"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 max-h-[calc(100vh-200px)] overflow-y-auto">
              <div className="bg-gradient-to-r from-emerald-500 to-green-500 -m-6 mb-6 p-6 rounded-t-2xl"><h3 className="text-2xl font-bold text-white mb-2">{currentCard.lab_category_name}</h3><div className="flex items-center gap-2"><Users className="w-4 h-4 text-emerald-100" /><p className="text-emerald-100">{currentCard.staff_size}</p></div></div>
              <div className="mb-6 p-5 bg-gradient-to-br from-emerald-50 to-green-50 rounded-xl"><div className="flex justify-between items-center"><div><p className="text-sm text-gray-600 mb-1">Monthly Investment</p><p className="text-3xl font-bold text-gray-900">{formatCurrency(parseFloat(currentCard.monthly_fee))}<span className="text-base font-normal text-gray-500">/month</span></p></div><div className="text-right"><p className="text-sm text-gray-600 mb-1">Annual Investment</p><p className="text-2xl font-bold text-emerald-600">{formatCurrency(parseFloat(currentCard.annual_total))}<span className="text-sm font-normal text-gray-500">/year</span></p><p className="text-xs text-emerald-500 mt-1">Save 2 months FREE!</p></div></div></div>
              <div className="mb-6"><div className="flex items-center gap-2 mb-4"><Gift className="w-5 h-5 text-emerald-500" /><h4 className="font-semibold text-gray-800 text-lg">Key Perks & Features</h4></div><div className="grid grid-cols-1 md:grid-cols-2 gap-3">{renderKeyPerks(currentCard.key_perks)}</div></div>
              <div className="pt-4 border-t border-gray-100"><div className="flex justify-between items-center text-sm text-gray-500"><div className="flex items-center gap-2"><Calendar className="w-4 h-4" /><span>Created: {formatDate(currentCard.created_at)}</span></div><div className="flex items-center gap-2"><Calendar className="w-4 h-4" /><span>Last Updated: {formatDate(currentCard.updated_at)}</span></div></div></div>
            </div>
            <div className="px-6 py-5 border-t border-gray-100 flex justify-end space-x-3 sticky bottom-0 bg-white rounded-b-2xl">
              <button onClick={() => { handleCloseViewModal(); handleEditCard(currentCard); }} className="px-6 py-2.5 bg-emerald-500 text-white rounded-xl hover:bg-emerald-600 transition-all duration-300 font-medium shadow-md hover:shadow-lg"><Edit className="w-4 h-4 inline mr-2" /> Edit Card</button>
              <button onClick={handleCloseViewModal} className="px-6 py-2.5 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-all duration-300 font-medium">Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Content Modal - JODIT EDITOR */}
      {isContentModalOpen && (
        <>
          <div className="fixed inset-0 z-[100] bg-black/50 backdrop-blur-sm animate-fadeIn" onClick={handleCloseContentModal} />
          <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 pointer-events-none">
            <div className="bg-white rounded-3xl shadow-2xl w-full max-w-6xl pointer-events-auto overflow-hidden animate-modalIn border border-gray-100">
              <div className="px-8 py-5 border-b flex justify-between items-center bg-gray-50/50">
                <h2 className="text-xl font-bold text-gray-800">
                  {isContentEditMode ? "Update Content" : "Create Content"}
                </h2>
                <button onClick={handleCloseContentModal} className="p-2 hover:bg-gray-200 rounded-full transition-colors">
                  <X size={20} />
                </button>
              </div>
              
              <form onSubmit={handleContentSubmit} className="p-8 space-y-6 overflow-y-auto max-h-[85vh]">
                {/* Content Editor */}
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2 uppercase tracking-wider">
                    Content Body <span className="text-red-500">*</span>
                  </label>
                  <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
                    <JoditEditor
                      ref={editorRef}
                      value={contentFormData.content}
                      config={config}
                      onChange={handleEditorChange}
                      onBlur={(newContent) => {
                        if (newContent !== contentFormData.content) {
                          setContentFormData(prev => ({...prev, content: newContent}));
                        }
                      }}
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-2">Tip: Use the toolbar above to format text, add images, create lists, change fonts, and more!</p>
                </div>
                
                {/* Status */}
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2 uppercase tracking-wider">
                    Status
                  </label>
                  <div className="flex items-center gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input 
                        type="radio" 
                        checked={contentFormData.is_published === true} 
                        onChange={() => setContentFormData(prev => ({ ...prev, is_published: true }))} 
                        className="w-4 h-4 text-emerald-600" 
                      />
                      <span className="text-gray-700">Published</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input 
                        type="radio" 
                        checked={contentFormData.is_published === false} 
                        onChange={() => setContentFormData(prev => ({ ...prev, is_published: false }))} 
                        className="w-4 h-4 text-gray-600" 
                      />
                      <span className="text-gray-700">Draft</span>
                    </label>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Only published content will be visible to visitors</p>
                </div>
                
                {/* Submit Button */}
                <button 
                  type="submit" 
                  className="w-full py-4 bg-gradient-to-r from-emerald-500 to-green-500 text-white rounded-2xl font-bold shadow-xl hover:brightness-110 transition-all text-lg flex items-center justify-center disabled:opacity-70 disabled:cursor-not-allowed"
                  disabled={saving}
                >
                  {saving ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      Saving...
                    </>
                  ) : isContentEditMode ? (
                    'Update Content'
                  ) : (
                    'Create Content'
                  )}
                </button>
              </form>
            </div>
          </div>
        </>
      )}

      {/* View Content Modal */}
      {isContentViewModalOpen && currentContent && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 overflow-y-auto">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl my-8">
            <div className="px-6 py-5 border-b border-gray-100 flex justify-between items-center sticky top-0 bg-white rounded-t-2xl">
              <div className="flex items-center gap-3">
                <h2 className="text-2xl font-bold text-gray-900">Content Details</h2>
                {currentContent.is_published ? (<span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800"><Globe className="w-3 h-3 mr-1" />Published</span>) : (<span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">Draft</span>)}
              </div>
              <button onClick={handleCloseContentViewModal} className="p-2 hover:bg-gray-100 rounded-xl transition-colors"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 max-h-[calc(100vh-200px)] overflow-y-auto">
              <div className="prose prose-lg max-w-none custom-description" dangerouslySetInnerHTML={{ __html: currentContent.content }} />
              <div className="mt-8 pt-6 border-t border-gray-100">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-500">
                  <div className="flex items-center gap-2"><Calendar className="w-4 h-4" /><span>Created: {formatDateTime(currentContent.created_at)}</span></div>
                  <div className="flex items-center gap-2"><Calendar className="w-4 h-4" /><span>Last Updated: {formatDateTime(currentContent.updated_at)}</span></div>
                </div>
              </div>
            </div>
            <div className="px-6 py-5 border-t border-gray-100 flex justify-end space-x-3 sticky bottom-0 bg-white rounded-b-2xl">
              <button onClick={() => { handleCloseContentViewModal(); handleEditContent(currentContent); }} className="px-6 py-2.5 bg-emerald-500 text-white rounded-xl hover:bg-emerald-600 transition-all duration-300 font-medium shadow-md hover:shadow-lg"><Edit className="w-4 h-4 inline mr-2" /> Edit Content</button>
              <button onClick={handleCloseContentViewModal} className="px-6 py-2.5 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-all duration-300 font-medium">Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Styles */}
      <style jsx>{`
        @keyframes fadeIn { 
          from { opacity: 0; transform: translateY(10px); } 
          to { opacity: 1; transform: translateY(0); } 
        }
        
        @keyframes modalIn { 
          from { opacity: 0; transform: scale(0.95); } 
          to { opacity: 1; transform: scale(1); } 
        }
        
        .animate-fadeIn { 
          animation: fadeIn 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards; 
        }
        
        .animate-modalIn { 
          animation: modalIn 0.4s cubic-bezier(0.16, 1, 0.3, 1) forwards; 
        }
        
        .custom-description * { 
          background-color: transparent !important; 
          margin-bottom: 1rem;
        }
        
        .custom-description p {
          font-size: 1rem;
          line-height: 1.6;
          color: #374151;
        }
        
        .custom-description ul, 
        .custom-description ol {
          padding-left: 1.5rem;
          margin-bottom: 1rem;
        }
        
        .custom-description li {
          margin-bottom: 0.5rem;
        }
        
        .custom-description img {
          max-width: 100%;
          height: auto;
          border-radius: 0.5rem;
        }
        
        /* Jodit Editor Custom Styling */
        :global(.jodit-container) {
          border: 1px solid #e5e7eb !important;
          border-radius: 16px !important;
          overflow: hidden !important;
        }
        
        :global(.jodit-workplace) {
          min-height: 350px !important;
        }
        
        :global(.jodit-toolbar) {
          background: #f9fafb !important;
          border-bottom: 1px solid #e5e7eb !important;
        }
        
        :global(.jodit-wysiwyg) {
          padding: 20px !important;
          min-height: 350px !important;
          background: #ffffff !important;
        }
        
        :global(.jodit-toolbar__box) {
          overflow-x: auto !important;
        }
      `}</style>
    </div>
  );
};

export default AddStoreCards;